/**
 * Step 5 — Hunger chips — SYNTHESIS variant
 *
 * Spec: specs/onboarding-rebuild.md → Act 1 step 5
 *
 * Synthesizes — this is the professional variant verbatim per the internal
 * review (the 40ms-delayed checkmark width-animation + selected-count
 * counter is already best-of-class; experimental's stones-scatter is
 * dropped — too on-the-nose for a multi-select chip surface).
 *
 * What synthesis lifts:
 *   - HungerChip component with 40ms-delayed checkmark slide-in (mounts +
 *     unmounts the width-animated slot — no jitter).
 *   - Selection counter that fades in at 1+ (never reserves space when 0).
 *   - Card-wrapped chip surface, hover lift via transform, CTA wake-up.
 *
 * What synthesis explicitly drops:
 *   - "Step 5 of 18" eyebrow (banned per Mason; humanizer log).
 */

import { useEffect, useRef, useState } from 'react'
import ContinueButton from '@/components/onboarding/ContinueButton'

const CHECK_FADE_DELAY_MS = 40
const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)'

// Module-scope HungerChip — extracted per static-components lint rule.
function HungerChip({ option, selected, onToggle }) {
  const [showCheck, setShowCheck] = useState(selected)
  const timeoutRef = useRef(null)

  useEffect(() => {
    clearTimeout(timeoutRef.current)
    if (selected) {
      timeoutRef.current = setTimeout(() => setShowCheck(true), CHECK_FADE_DELAY_MS)
    } else {
      setShowCheck(false)
    }
    return () => clearTimeout(timeoutRef.current)
  }, [selected])

  return (
    <button
      type="button"
      onClick={() => onToggle(option.value)}
      aria-pressed={selected}
      className="hc-syn-chip group inline-flex items-center gap-1.5 rounded-full text-[14px] font-medium tracking-[0.005em] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gold-select)] focus-visible:ring-offset-0"
      style={{
        padding: '10px 16px',
        background: selected ? 'var(--gold-select)' : 'var(--bg-surface)',
        border: `1px solid ${selected ? 'var(--gold)' : 'var(--glass-border)'}`,
        color: selected ? 'var(--gold)' : 'var(--text-primary)',
        boxShadow: selected
          ? '0 1px 0 rgba(255,255,255,0.04) inset, 0 4px 14px -8px var(--gold-select)'
          : '0 1px 0 rgba(255,255,255,0.02) inset',
        transition: `background 220ms ${EASE}, border-color 220ms ${EASE}, color 220ms ${EASE}, transform 220ms ${EASE}, box-shadow 220ms ${EASE}`,
      }}
    >
      {/* Fixed-width checkmark slot — width animates 0 → 14px after a 40ms
          beat. Chip width doesn't jitter on selection. */}
      <span
        aria-hidden
        className="overflow-hidden inline-flex items-center justify-center"
        style={{
          width: showCheck ? 14 : 0,
          opacity: showCheck ? 1 : 0,
          transform: showCheck ? 'scale(1)' : 'scale(0.6)',
          transition: `width 200ms ${EASE}, opacity 180ms ${EASE}, transform 200ms ${EASE}`,
        }}
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M2.5 6.5L5 9L9.5 3.5" />
        </svg>
      </span>
      <span className="leading-none">{option.label}</span>
    </button>
  )
}

export default function HungerChipsSynthesis({ value, onToggle, onContinue, options }) {
  const count = value.length
  const hasSelection = count > 0

  return (
    <div className="w-full max-w-xl md:max-w-2xl lg:max-w-3xl mx-auto px-6 py-10 sm:py-14">
      <style>{`
        .hc-syn-chip:hover {
          transform: translateY(-1px);
          border-color: var(--gold-dim) !important;
        }
        .hc-syn-chip[aria-pressed="true"]:hover {
          border-color: var(--gold) !important;
          box-shadow: 0 1px 0 rgba(255,255,255,0.04) inset, 0 6px 18px -8px var(--gold-select) !important;
        }
        .hc-syn-chip:active {
          transform: translateY(0);
        }
        .hc-syn-cta {
          transition: transform 280ms cubic-bezier(0.16, 1, 0.3, 1),
                      box-shadow 280ms cubic-bezier(0.16, 1, 0.3, 1),
                      background 220ms ease-out,
                      opacity 220ms cubic-bezier(0.16, 1, 0.3, 1);
        }
        .hc-syn-cta:not(:disabled):hover {
          transform: translateY(-1px);
          box-shadow: 0 10px 30px -8px var(--gold-select),
                      0 2px 0 rgba(0,0,0,0.05) inset;
        }
        .hc-syn-cta:not(:disabled):active { transform: translateY(0); }
        .hc-syn-counter {
          transition: opacity 220ms cubic-bezier(0.16, 1, 0.3, 1),
                      transform 220ms cubic-bezier(0.16, 1, 0.3, 1);
        }
      `}</style>

      {/* Heading */}
      <h1 className="font-heading text-[28px] sm:text-[34px] leading-[1.15] text-[var(--text-primary)] text-center tracking-tight">
        What are you wrestling with?
      </h1>
      <p className="mt-3 text-[15px] text-[var(--text-muted)] text-center max-w-md mx-auto leading-relaxed">
        Pick all that apply.
      </p>

      {/* Counter — fades in at 1+ selected, never reserves space when 0 */}
      <div
        className="hc-syn-counter mt-5 text-center"
        style={{
          opacity: hasSelection ? 1 : 0,
          transform: hasSelection ? 'translateY(0)' : 'translateY(-4px)',
        }}
        aria-live="polite"
      >
        <span className="text-[12px] tracking-[0.14em] uppercase text-[var(--text-muted-soft)] tabular-nums">
          {count} selected
        </span>
      </div>

      {/* Chips card */}
      <div
        className="mt-6 rounded-2xl px-5 py-6 sm:px-7 sm:py-7"
        style={{
          background: 'var(--bg-surface)',
          border: '1px solid var(--glass-border)',
          boxShadow: '0 1px 0 rgba(255,255,255,0.03) inset, 0 20px 40px -24px rgba(0,0,0,0.5)',
        }}
      >
        <div className="flex flex-wrap gap-2.5 justify-center">
          {options.map(opt => (
            <HungerChip
              key={opt.value}
              option={opt}
              selected={value.includes(opt.value)}
              onToggle={onToggle}
            />
          ))}
        </div>
      </div>

      {/* CTA — always enabled. User can continue without picking anything
          per Mason's call. ContinueButton inherits the standard gold
          treatment; the conditional label preserves the "Skip for now"
          fallback when nothing is selected. */}
      <div className="mt-9 flex justify-center">
        <ContinueButton onClick={onContinue}>
          {hasSelection ? 'Continue' : 'Skip for now'}
        </ContinueButton>
      </div>

      <p className="mt-5 text-center text-[11px] text-[var(--text-muted-soft)] tracking-wide">
        You can change these later.
      </p>
    </div>
  )
}
